import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { ArrowLeft, Calendar, DollarSign, AlertCircle, ShoppingCart, FileText } from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MinhasFaturas = ({ user }) => {
  const navigate = useNavigate();
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.isLoggedIn) {
      navigate('/login');
      return;
    }
    loadCompras();
  }, [user, navigate]);

  const loadCompras = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API}/knorr/clientes/${user.clienteId}/compras-vendas`, {
        timeout: 30000
      });
      setCompras(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar compras:', err);
      setError('Erro ao carregar suas compras. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const gerarPDF = (item) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Cores
    const azulEscuro = [25, 42, 86];
    const vermelho = [238, 29, 37];
    
    // Header - fundo azul
    doc.setFillColor(...azulEscuro);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Título
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('KNORR LEILÕES', pageWidth / 2, 18, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('FATURA DE COMPRA', pageWidth / 2, 30, { align: 'center' });
    
    // Linha vermelha
    doc.setFillColor(...vermelho);
    doc.rect(0, 40, pageWidth, 3, 'F');
    
    let y = 55;
    
    // Info do Leilão
    doc.setTextColor(...azulEscuro);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('DADOS DO LEILÃO', 14, y);
    y += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    
    const leilaoData = [
      ['Leilão:', item.leilao || '-'],
      ['Data:', formatDate(item.datlei)],
      ['Código Leilão:', String(item.idleilao || '-')],
      ['Nota:', String(item.codnot || '-')]
    ];
    
    leilaoData.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 55, y);
      y += 6;
    });
    
    y += 6;
    
    // Info do Comprador
    doc.setTextColor(...azulEscuro);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('DADOS DO COMPRADOR', 14, y);
    y += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    
    const compradorData = [
      ['Nome:', item.nomexx || user.nome || '-'],
      ['CPF:', item.cpfxxx || user.cpf || '-'],
      ['ID Cliente:', String(item.idcli || user.clienteId || '-')]
    ];
    
    compradorData.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 55, y);
      y += 6;
    });
    
    y += 6;
    
    // Info do Lote
    doc.setTextColor(...azulEscuro);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('DADOS DO LOTE', 14, y);
    y += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    
    const loteData = [
      ['Lote Nº:', String(item.lotexx || '-')],
      ['Descrição:', item.deslot || '-'],
      ['Quantidade:', String(item.qtdxxx || 1)],
      ['ID Lote:', String(item.idlote || '-')]
    ];
    
    loteData.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), 55, y);
      y += 6;
    });
    
    y += 8;
    
    // Tabela de Valores (manual)
    doc.setTextColor(...azulEscuro);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('VALORES', 14, y);
    y += 8;
    
    const valores = [
      ['Valor Original', formatCurrency(item.valororiginal)],
      ['Desconto', formatCurrency(item.valordesconto)],
      ['Valor a Pagar', formatCurrency(item.valorpagar)],
      ['Valor da Parcela', formatCurrency(item.vlrpar)],
      ['Valor Total', formatCurrency(item.vlrtot)],
      ['Comissão (' + (item.comissao || 0) + '%)', formatCurrency(item.valorcomissao)],
    ];
    
    // Header da tabela
    doc.setFillColor(...azulEscuro);
    doc.rect(14, y - 4, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Descrição', 18, y);
    doc.text('Valor', 170, y, { align: 'right' });
    y += 8;
    
    // Linhas da tabela
    valores.forEach(([desc, val], idx) => {
      if (idx % 2 === 0) {
        doc.setFillColor(245, 247, 250);
        doc.rect(14, y - 4, 180, 7, 'F');
      }
      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'normal');
      doc.text(desc, 18, y);
      doc.setFont('helvetica', 'bold');
      doc.text(val, 170, y, { align: 'right' });
      y += 7;
    });
    
    // Borda da tabela
    doc.setDrawColor(200, 200, 200);
    doc.rect(14, y - 4 - (valores.length * 7) - 8, 180, (valores.length * 7) + 8, 'S');
    
    y += 10;
    
    // Info de Pagamento
    doc.setTextColor(...azulEscuro);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('PAGAMENTO', 14, y);
    y += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    
    const pagamentoData = [
      ['Forma de Pagamento:', item.forma_pagamento || '-'],
      ['Status Parcela:', item.st_parcela === 'A' ? 'Aberta' : 'Paga'],
      ['Status Comissão:', item.st_comissao === 'A' ? 'Aberta' : 'Paga'],
      ['Status Contrato:', item.st_contrato === 'A' ? 'Aberto' : 'Fechado'],
      ['Status Embarque:', item.st_embarque === 'A' ? 'Aberto' : 'Concluído'],
      ['Defesa:', item.defesa === 'S' ? 'Sim' : 'Não'],
      ['Condição Pagamento:', String(item.idcondpagto || '-')]
    ];
    
    pagamentoData.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 75, y);
      y += 6;
    });
    
    // Valor destaque
    y += 8;
    doc.setFillColor(245, 247, 250);
    doc.rect(14, y - 5, pageWidth - 28, 16, 'F');
    doc.setDrawColor(...azulEscuro);
    doc.rect(14, y - 5, pageWidth - 28, 16, 'S');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...azulEscuro);
    doc.text('VALOR A PAGAR:', 20, y + 5);
    doc.setTextColor(...vermelho);
    doc.setFontSize(16);
    doc.text(formatCurrency(item.valorpagar), pageWidth - 20, y + 5, { align: 'right' });
    
    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 20;
    doc.setFillColor(...azulEscuro);
    doc.rect(0, footerY - 5, pageWidth, 25, 'F');
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Knorr Leilões - Tradição, modernidade e eficiência desde 1960', pageWidth / 2, footerY + 3, { align: 'center' });
    doc.text(`Documento gerado em ${new Date().toLocaleString('pt-BR')}`, pageWidth / 2, footerY + 9, { align: 'center' });
    doc.text('WhatsApp: (53) 98114-6431 | contato@knorrleiloes.com.br', pageWidth / 2, footerY + 15, { align: 'center' });
    
    // Salvar
    const nomeArquivo = `fatura_lote_${item.lotexx}_${item.leilao?.replace(/[^a-zA-Z0-9]/g, '_')?.substring(0, 30) || 'leilao'}.pdf`;
    doc.save(nomeArquivo);
  };

  // Agrupar compras por leilão
  const comprasPorLeilao = compras.reduce((acc, compra) => {
    const key = compra.idleilao;
    if (!acc[key]) {
      acc[key] = {
        idleilao: compra.idleilao,
        leilao: compra.leilao,
        datlei: compra.datlei,
        itens: []
      };
    }
    acc[key].itens.push(compra);
    return acc;
  }, {});

  const leiloesAgrupados = Object.values(comprasPorLeilao).sort((a, b) => 
    new Date(b.datlei) - new Date(a.datlei)
  );

  if (!user?.isLoggedIn) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[rgb(25,42,86)] mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando suas compras...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b-4 border-[rgb(25,42,86)]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-black mb-2 text-[rgb(25,42,86)]" data-testid="page-title">MINHAS COMPRAS</h1>
            <p className="text-lg opacity-80 font-semibold">Histórico de compras em leilões</p>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {error && (
          <Alert className="border-[rgb(238,29,37)] bg-red-50 mb-6">
            <AlertCircle className="h-4 w-4 text-[rgb(238,29,37)]" />
            <AlertDescription className="text-[rgb(238,29,37)]">{error}</AlertDescription>
          </Alert>
        )}

        {compras.length === 0 && !error ? (
          <Card className="text-center py-12">
            <CardContent>
              <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">Nenhuma compra encontrada</h3>
              <p className="text-gray-500">Você ainda não possui compras registradas em leilões.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Resumo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-600">Total de Lotes</p>
                  <p className="text-3xl font-black text-[rgb(25,42,86)]" data-testid="total-lotes">{compras.length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-600">Leilões</p>
                  <p className="text-3xl font-black text-[rgb(25,42,86)]" data-testid="total-leiloes">{leiloesAgrupados.length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-600">Valor Total</p>
                  <p className="text-2xl font-black text-[rgb(238,29,37)]" data-testid="total-valor">
                    {formatCurrency(compras.reduce((sum, c) => sum + (c.valorpagar || 0), 0))}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Compras agrupadas por leilão */}
            {leiloesAgrupados.map((grupo) => (
              <Card key={grupo.idleilao} className="overflow-hidden" data-testid={`leilao-group-${grupo.idleilao}`}>
                <CardHeader className="bg-[rgb(25,42,86)] text-white py-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold flex items-center">
                      <Calendar className="h-5 w-5 mr-2" />
                      {grupo.leilao}
                    </CardTitle>
                    <Badge className="bg-white text-[rgb(25,42,86)] font-bold">
                      {formatDate(grupo.datlei)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Lote</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Descrição</th>
                          <th className="px-4 py-3 text-center font-semibold text-gray-700">Qtd</th>
                          <th className="px-4 py-3 text-right font-semibold text-gray-700">Parcela</th>
                          <th className="px-4 py-3 text-right font-semibold text-gray-700">Total</th>
                          <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                          <th className="px-4 py-3 text-center font-semibold text-gray-700">Fatura</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {grupo.itens.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-mono font-bold text-[rgb(25,42,86)]">{item.lotexx}</td>
                            <td className="px-4 py-3 text-gray-800">{item.deslot}</td>
                            <td className="px-4 py-3 text-center">{item.qtdxxx}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(item.vlrpar)}</td>
                            <td className="px-4 py-3 text-right font-semibold text-[rgb(238,29,37)]">
                              {formatCurrency(item.valorpagar)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={item.st_parcela === 'A' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                                {item.st_parcela === 'A' ? 'Aberta' : 'Paga'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => gerarPDF(item)}
                                className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white"
                                title="Gerar PDF da Fatura"
                                data-testid={`btn-pdf-${item.idlote}`}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t-2">
                        <tr>
                          <td colSpan="4" className="px-4 py-3 text-right font-bold text-gray-700">
                            Total do Leilão:
                          </td>
                          <td className="px-4 py-3 text-right font-black text-[rgb(238,29,37)] text-lg">
                            {formatCurrency(grupo.itens.reduce((sum, i) => sum + (i.valorpagar || 0), 0))}
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MinhasFaturas;
